import React, { useCallback, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  APIActions,
  Box,
  Button,
  CustomDialog,
  Icon,
  IconName,
  MUIDataTable,
  MUIDataTableColumn,
  stringToLongDateAndShortTimeWithoutCommaDisplay,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  Typography,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { ChangeSummary } from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import TaskFileBadge from 'web-library/components/TaskFileBadge';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
  ProfileAmendmentRequestsResponse,
  useGetProfileAmendmentRequests,
} from '../../../../api-refactored/profile-amendment-requests/useGetProfileAmendmentRequests';
import {
  ProfileAmendmentRequestStatus,
  usePutCancelProfileAmendmentRequest,
} from '../../../../api-refactored/profile-amendment-requests/usePutCancelProfileAmendmentRequest';
import { useGetDocumentUploadStatus } from '../../../../api-refactored/document/useGetDocumentUploadStatus';
import { displayProfileAmendmentRequestPillStatus } from '../../../admin/components/ProfileAmendmentRequest/ProfileAmendmentRequestsDataTable';
import RouteKey from '../../../../routes/key';
import { FileUploadState } from '../../../../models/File';

const { profile } = RouteKey.child;
const { candidateDetail, uploadSupportingDocuments } = RouteKey.profile.child;

const useStyles = makeStyles()((theme) =>
({
  changeRequestSectionTitle: {
    marginBottom: theme.spacing(4),
  },
  changeSummaryContainer: {
    marginBottom: theme.spacing(2),
  },
  changeSummaryTitle: {
    color: theme.color.text.textTertiary,
    '&:nth-child(3), &:nth-child(4)': {
      marginTop: theme.spacing(2),
    },
  },
  remarks: {
    overflow: 'hidden',
    maxWidth: '18ch',
    wordWrap: 'break-word',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  button: {
    padding: '0 !important',
    whiteSpace: 'nowrap',
  },
  cancelButton: {
    color: theme.color.state.stateError,
  },
  noActionDisplay: {
    marginTop: theme.spacing(2),
  },
}));

const tableColumns = ({
  data,
  setAmendmentCancelRequestUuid,
  setFileUuid,
  isLoadingGetDocumentUploadStatus,
  classes,
  history,
  t,
  theme,
}: {
  data: ProfileAmendmentRequestsResponse[];
  setAmendmentCancelRequestUuid: React.Dispatch<React.SetStateAction<string>>;
  setFileUuid: React.Dispatch<React.SetStateAction<string>>;
  isLoadingGetDocumentUploadStatus: boolean;
  classes: Record<string,string>;
  history: RouteComponentProps['history'];
  t: TFunction<'translation'>;
  theme: AugmentedTheme;
}): MUIDataTableColumn[] => [
    {
      name: 'requestDateTime',
      label: t('profile->>CreateCandidateForm.RequestDateTime'),
      options: {
        customBodyRenderLite: (dataIndex: number) =>
          stringToLongDateAndShortTimeWithoutCommaDisplay(data[dataIndex].requestDateTime),
      },
    },
    {
      name: 'changeSummary',
      label: t('admin->>UserGroups.ChangeSummary'),
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const { changeSummary } = data[dataIndex];

          if (changeSummary?.length) {
            return changeSummary.map((change: ChangeSummary, index: number) => (
              <Box
                className={index === changeSummary.length - 1 ? '' : classes.changeSummaryContainer}
                key={change.baseFieldName}
              >
                <Typography className={classes.changeSummaryTitle} variant="subtitle2">
                  {change.baseFieldName}
                </Typography>
                <Typography variant="body2">{`Old value: ${change.baseFieldName === 'PR Status' && change.before === 'ALIEN'
                  ? 'FOREIGNER'
                  : change.before
                  }`}</Typography>
                <Typography variant="body2">{`New value: ${change.baseFieldName === 'PR Status' && change.after === 'ALIEN'
                  ? 'FOREIGNER'
                  : change.after
                  }`}</Typography>
              </Box>
            ));
          }
          return '-';
        },
      },
    },
    {
      name: 'requestorRemarks',
      label: t('profile->>CreateCandidateForm.RequestorRemarks'),
      options: {
        customBodyRenderLite: (dataIndex: number) => (
          <Typography className={classes.remarks} variant="body2">
            {data[dataIndex].requestorRemarks || '-'}
          </Typography>
        ),
      },
    },
    {
      name: 'approverRemarks',
      label: t('profile->>CreateCandidateForm.ApproverRemarks'),
      options: {
        customBodyRenderLite: (dataIndex: number) => (
          <Typography className={classes.remarks} variant="body2">
            {data[dataIndex].approverRemarks || '-'}
          </Typography>
        ),
      },
    },
    {
      name: 'status',
      label: t('profile->>CreateCandidateForm.Status'),
      options: {
        customBodyRenderLite: (dataIndex: number) => (
          <>{displayProfileAmendmentRequestPillStatus(data[dataIndex].status)}</>
        ),
      },
    },
    {
      name: 'actions',
      label: t('profile->>CreateCandidateForm.Actions'),
      options: {
        setCellProps: () => ({ style: { paddingTop: theme.spacing(1) } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            {(data[dataIndex].status === ProfileAmendmentRequestStatus.Approved ||
              data[dataIndex].status === ProfileAmendmentRequestStatus.Cancelled ||
              data[dataIndex].status === ProfileAmendmentRequestStatus.Rejected) && (
                <Box className={classes.noActionDisplay}>-</Box>
              )}
            <Box className={classes.buttonContainer}>
              {data[dataIndex].status === ProfileAmendmentRequestStatus.Incomplete && (
                <Button
                  className={classes.button}
                  onClick={() =>
                    history.push(
                      `/${profile}/${candidateDetail.route}/${data[dataIndex].uuid}/${uploadSupportingDocuments.route}`
                    )
                  }
                  startIcon={<Icon color="primary" name={IconName.export} size="S" />}
                  variant="text"
                  color="primary"
                >
                  {t('profile->>CreateCandidateForm.AddSupportingDocuments')}
                </Button>
              )}
              {(data[dataIndex].status === ProfileAmendmentRequestStatus.Pending ||
                data[dataIndex].status === ProfileAmendmentRequestStatus.Incomplete) && (
                  <Button
                    className={`${classes.button} ${classes.cancelButton}`}
                    onClick={() => setAmendmentCancelRequestUuid(data[dataIndex].uuid)}
                    startIcon={<Icon color="secondary" name={IconName.cross} size="S" />}
                    variant="text"
                    color="secondary"
                  >
                    {t('profile->>CreateCandidateForm.CancelRequest')}
                  </Button>
                )}
              <Box>
                {data[dataIndex].files.length > 0 ? (
                  <>
                    {data[dataIndex].files.map((f) => (
                      <TaskFileBadge fullWidth key={f.fileUuid} file={f} setFileUuid={setFileUuid} />
                    ))}
                  </>
                ) : (
                  '-'
                )}
              </Box>
            </Box>
          </>
        ),
      },
    },
  ];

const CandidateProfileChangeRequestDataTable = ({
  refetchCandidateProfileDetails,
}: {
  refetchCandidateProfileDetails: () => void;
}) => {
  const { classes } = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
  });
  const { snackbarHandler } = useSnackbarHandler();
  const [amendmentCancelRequestUuid, setAmendmentCancelRequestUuid] = useState<string>('');
  const [fileUuid, setFileUuid] = useState<string>('');

  const handleCloseAmendmentCancelRequest = useCallback(
    () => setAmendmentCancelRequestUuid(''),
    []
  );

  const {
    refetch: refetchProfileAmendmentRequests,
    isLoading: isLoadingProfileAmendmentRequests,
  } = useGetProfileAmendmentRequests({
    onSuccess: (res) =>
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res,
          count: res.length,
        },
      } as TableActionTypes),
  });

  const { mutate: cancelProfileAmendmentRequest } = usePutCancelProfileAmendmentRequest({
    onSuccess: () => {
      handleCloseAmendmentCancelRequest();
      refetchCandidateProfileDetails();
      refetchProfileAmendmentRequests();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('profile->>CreateCandidateForm.ChangeRequestCancelledMessage'),
      });
    },
    onError: handleCloseAmendmentCancelRequest,
  });

  const { isLoading: isLoadingGetDocumentUploadStatus } = useGetDocumentUploadStatus({
    fileKey: fileUuid,
    enabled: !!fileUuid,
    refetchInterval: undefined,
    onSuccess: (data) => {
      if (data.fileStatus === FileUploadState.SUCCESS) {
        window.open(data.url, '_blank');
        setFileUuid('');
      }
    },
  });

  const handleProceedAmendmentCancelRequest = useCallback(
    () =>
      cancelProfileAmendmentRequest({
        uuid: amendmentCancelRequestUuid,
        status: ProfileAmendmentRequestStatus.Cancelled,
      }),
    [amendmentCancelRequestUuid, cancelProfileAmendmentRequest]
  );

  return (
    <>
      <Typography className={classes.changeRequestSectionTitle} variant="h3">
        {t('admin->>UserGroups.ChangeRequest')}
      </Typography>
      <MUIDataTable
        isLoading={isLoadingProfileAmendmentRequests}
        key="changeRequestDataTable"
        state={state}
        dispatch={tableDispatch}
        columns={tableColumns({
          data: state.data,
          setAmendmentCancelRequestUuid,
          setFileUuid,
          isLoadingGetDocumentUploadStatus,
          classes,
          history,
          t,
          theme,
        })}
        data={state.data}
        options={{
          ...tOptions,
        }}
      />
      <CustomDialog
        open={!!amendmentCancelRequestUuid}
        title={t('profile->>CreateCandidateForm.CancelRequestTitle')}
        message={t('profile->>CreateCandidateForm.CancelRequestStatement')}
        action={
          <APIActions
            onClose={handleCloseAmendmentCancelRequest}
            apiCall={handleProceedAmendmentCancelRequest}
            cancelString={t('profile->>CreateCandidateForm.Cancel')}
            apiString={t('profile->>CreateCandidateForm.Proceed')}
          />
        }
      />
    </>
  );
};

export default CandidateProfileChangeRequestDataTable;
